import { AVO_EVENT_NAMES } from "avo/eventNames";
import React, { useContext, useEffect, useState } from "react";
import { sendEventUsingAvo } from "./events";
import { useSuiTurnstileState } from "schoolUiComponents/SuiTurnstile";
import { GlobalSpinner } from "./utils";

const commonError = "Something went wrong. Please try again later.";
let waitingForSuiToken = false;
export interface ITeacherSignupProps {
    firstName: string;
    lastName: string;
    schoolEmail: string;
    password: string;
    yearOfBirth?: string;
    turnstileToken: string;
    termsAndConditions?: boolean;
    emailPreference?: boolean;
}

export interface IErrorStates {
    firstName: boolean;
    lastName: boolean;
    schoolEmail: boolean;
    password: boolean;
    yearOfBirth: string | boolean;
    common: string;
}
interface ITeacherSignupState {
    sendEvent: (clickedOn: string) => void;
    getFromPage: () => string | null;
    makeTeacherSignup: () => Promise<void>;
    isTncPopupVisible: boolean;
    toggleTnCPopup: (val: boolean) => void;
    firstName: string;
    lastName: string;
    schoolEmail: string;
    password: string;
    yearOfBirth: string;
    errorStates: IErrorStates;
    setFirstName: (val: string) => void;
    setLastName: (val: string) => void;
    setSchoolEmail: (val: string) => void;
    setPassword: (val: string) => void;
    setYearOfBirth: (val: string) => void;
    setErrorStates: (val: any) => any;
    isUkUser: boolean;
    isTermsAndConditionsChecked: boolean;
    isNewsLetterChecked: boolean;
    toggleIsTermsAndConditionsChecked: (val: boolean) => void;
    toggleIsNewsLetterChecked: (val: boolean) => void;
    isSubmitLoading: boolean;
};

// @ts-ignore
export const IS_EU_USER = window?.is_europe_teacher;

// @ts-ignore
export const isUkUser = window?.is_uk_user;

const TeacherSignupContext = React.createContext({} as ITeacherSignupState);

const TeacherSignupProvider = ({ children }: any) => {

    const { token, refreshToken, isTurnstileLoading } = useSuiTurnstileState();
    const [isTncPopupVisible, toggleTnCPopup] = useState<boolean>(false);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    // @ts-ignore
    const [schoolEmail, setSchoolEmail] = useState<string>(window?.teacher_email || '');
    const [password, setPassword] = useState<string>('');
    const [yearOfBirth, setYearOfBirth] = useState<string>('');
    const [errorStates, setErrorStates] = useState<IErrorStates>({
        firstName: false,
        lastName: false,
        schoolEmail: false,
        password: false,
        yearOfBirth: false,
        common: ''
    })
    const [isTermsAndConditionsChecked, toggleIsTermsAndConditionsChecked] = useState<boolean>(false);
    const [isNewsLetterChecked, toggleIsNewsLetterChecked] = useState<boolean>(false);
    const [isSubmitLoading, toggleIsSubmitLoading] = useState<boolean>(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const isSplashleague = searchParams.get("splash_league") === "true" ? true : false;
        if(isSplashleague) {
            sessionStorage.setItem("signup_via_splashleague",  "true");
        }
    }, [window.location.search])

    useEffect(() => {
        return () => {
            waitingForSuiToken = false;
        }
    }, [])

    useEffect(() => {
        if(waitingForSuiToken) {
            waitingForSuiToken = false;
            makeTeacherSignup();
        }
    }, [isTurnstileLoading])

    const sendEvent = (clickedOn: string) => {
        sendEventUsingAvo({
            eventName: AVO_EVENT_NAMES.interactedLoginScreen,
            props: {
                clickedOn
            }
        })
    }

    const getFromPage = () => {
        if (window.location.hash.includes('?')) {
            const queryString = window.location.hash.split('?')[1];
            const urlParams = new URLSearchParams(queryString);
            const fromPage = urlParams.get('fromPage');
            if (fromPage) {
                return decodeURIComponent(fromPage);
            }
        }
        return null;
    }

    const makeTeacherSignup = async (): Promise<void> => {
        if(isTurnstileLoading) {
            waitingForSuiToken = true;
            toggleIsSubmitLoading(true);
            // GlobalSpinner.show();
            return;
        }
        const searchParams = new URLSearchParams(window.location.search);
        const via_contest = searchParams.get("via_contest");
        const formData = new URLSearchParams({
            utf8: '✓',
            'user[type]': 'teacher',
            'user[time_zone]': Intl.DateTimeFormat().resolvedOptions().timeZone,
            'user[tnc_privacy]': IS_EU_USER ? (isTermsAndConditionsChecked ? '1' : '0') : '0',
            'user[email_preference]': IS_EU_USER ? (isNewsLetterChecked ? '1' : '0') : '0',
            'user[first_name]': firstName,
            'user[last_name]': lastName,
            'user[is_bot_detected]': 'false',
            'user[email]': schoolEmail,
            'user[password]': password,
            'user[dob_year]': yearOfBirth,
            'cf-turnstile-response': token,
            'authenticity_token': document.querySelector('meta[name=csrf-token]')?.getAttribute("content"),
            commit: 'Sign up',
        });
        if(via_contest) {
            formData.append("via_contest", via_contest)
        }
        try {
            toggleIsSubmitLoading(true);
            // GlobalSpinner.show();
            const response = await fetch('/teachers/register.json?skip_institute=1', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData.toString(),
            });
            try {
                if (response.ok) {
                    const data = await response.json();
                    setTimeout(() => {
                        window.location.href = data?.redirect_url || "/";
                        // GlobalSpinner.hide();
                    }, 1000);
                } else {
                    refreshToken();
                    toggleIsSubmitLoading(false);
                    // GlobalSpinner.hide();
                    const data = await response.json();
                    setErrorStates({ ...errorStates, common: data.error || data.errors?.[0] || commonError })
                }
            } catch (e) {
                toggleIsSubmitLoading(false);
                refreshToken();
                // GlobalSpinner.hide();
                setErrorStates({ ...errorStates, common: commonError })
                showFlashMessage("Something went wrong. Please try again later.", 'error')
            }
        } catch (error) {
            refreshToken();
            toggleIsSubmitLoading(false);
            // GlobalSpinner.hide();
            console.error('Signup failed:', error);
            throw error;
        } finally {
        }

    }

    const TeacherSignupState: ITeacherSignupState = {
        sendEvent,
        getFromPage,
        makeTeacherSignup,
        isTncPopupVisible,
        toggleTnCPopup,
        firstName,
        lastName,
        schoolEmail,
        password,
        yearOfBirth,
        errorStates,
        setFirstName,
        setLastName,
        setSchoolEmail,
        setPassword,
        setYearOfBirth,
        setErrorStates,
        isTermsAndConditionsChecked,
        isUkUser,
        isNewsLetterChecked,
        toggleIsTermsAndConditionsChecked,
        toggleIsNewsLetterChecked,
        isSubmitLoading
    };
    return (
        <TeacherSignupContext.Provider value={TeacherSignupState}>
            {children}
        </TeacherSignupContext.Provider>
    );
}

export const useTeacherSignupState = () => {
    const context = useContext(TeacherSignupContext);
    if (!context) {
        throw Error(`Use ${useTeacherSignupState.name} in ${TeacherSignupContext.displayName}`);
    }
    return context;
};

export default TeacherSignupProvider;